import { LOGO_SRC, LOGO_WEBP } from "@/utils/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, LogIn } from "lucide-react";
import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import constructionPaperFlower from "@/assets/construction-paper-flower.png";
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { useAuth } from "@/components/AuthContext";
import { productionLogger } from '@/utils/production-logger';

const BohoFooter = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const showHomeLogo = location.pathname === "/";
  
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Require authentication for newsletter signup
    if (!user) {
      toast.error("Please sign in to subscribe to our newsletter and get exclusive offers!");
      return;
    }

    setIsSubscribing(true);

    try {
      // Use the secure newsletter subscription function
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { 
          email: user.email, // Use authenticated user's email
          source: 'footer',
          user_id: user.id // Pass user ID for authentication
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        toast.success("Newsletter subscription successful! ✨");
        setEmail(""); // Clear the form
      } else {
        toast.error(data?.error || "Subscription failed");
      }
    } catch (error: any) {
      productionLogger.error('Newsletter subscription error:', error);
      
      // Check if already subscribed based on error message
      const errorMessage = error?.message || '';
      if (errorMessage.includes('already subscribed')) {
        toast.info("You're already subscribed! Check your inbox for our latest updates.");
      } else {
        toast.error("Connection error. Please check your internet and try again.");
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="relative z-50 bg-gradient-earth border-t border-border">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center justify-start gap-3">
              <img 
                src={LOGO_SRC}
                alt="Boho Style Co. - Lifestyle Brand Est. 1976" 
                className="h-24 w-auto filter drop-shadow-sm dark:brightness-0 dark:invert"
                onError={(e) => {
                  e.currentTarget.src = "/lovable-uploads/bohostyleco-logo-1000x600-bl.png";
                }}
              />
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Embrace your authentic self with our curated collection of 
              bohemian-inspired clothing, home and lifestyle essentials.
            </p>
          </div>

          {/* Shop */}
          <div className="content-spacing-standard">
            <h4 className="section-header weight-semibold text-primary-color">Shop</h4>
            <div className="content-spacing-tiny">
                {[
                  { name: "All Products", href: "/products" },
                  { name: "Collections", href: "/collections" },
                  { name: "Goddess Collection", href: "/collections/goddess" },
                  { name: "Chrome Hearts", href: "/collections/chrome-hearts" }
                ].map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {item.name}
                  </Link>
                ))}
            </div>
          </div>

          {/* Discover */}
          <div className="content-spacing-standard">
            <h4 className="section-header weight-semibold text-primary-color">Discover</h4>
            <div className="content-spacing-tiny">
                {[
                  { name: "About Us", href: "/about" },
                  { name: "Blog & Style Guides", href: "/blog" },
                  { name: "Slow Living", href: "/slow-living" }
                ].map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {item.name}
                  </Link>
                ))}
            </div>
          </div>

          {/* Customer Care */}
          <div className="content-spacing-standard">
            <h4 className="section-header weight-semibold text-primary-color">Customer Care</h4>
            <div className="content-spacing-tiny">
                {[
                  { name: "Contact Us", href: "/contact" },
                  { name: "Shipping Info", href: "/shipping-info" },
                  { name: "Returns & FAQ", href: "/faq" },
                  { name: "Size Guides", href: "/size-guide/clothing" },
                  { name: "Track Order", href: "/track-order" },
                  { name: "Sitemap", href: "/sitemap" }
                ].map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {item.name}
                  </Link>
                ))}
            </div>
          </div>

          {/* Newsletter */}
          <div className="content-spacing-standard">
            <h4 className="section-header weight-semibold text-primary-color">Stay Connected</h4>
            <p className="body-text text-muted-color">
              Subscribe to receive exclusive offers and bohemian style inspiration.
            </p>
            <form onSubmit={handleSubscribe} className="content-spacing-compact">
              {user ? (
                <>
                  <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-md">
                    <Mail className="h-4 w-4 text-primary" />
                    <span className="text-foreground">{user.email}</span>
                  </div>
                  <Button 
                    type="submit" 
                    variant="default" 
                    className="w-full bg-primary text-primary-foreground hover:bg-primary-glow text-sm px-3 py-2"
                    disabled={isSubscribing}
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    {isSubscribing ? "Subscribing..." : "Subscribe"}
                  </Button>
                </>
              ) : (
                <div className="content-spacing-compact">
                  <Input
                    id="newsletter-email"
                    name="email"
                    type="email"
                    placeholder="Sign in to subscribe"
                    value=""
                    className="h-9 text-sm bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20"
                    disabled
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    className="w-full text-sm px-3 py-2"
                    asChild
                  >
                    <Link to="/auth">
                      <LogIn className="h-3 w-3 mr-1" />
                      Sign In to Subscribe
                    </Link>
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Get exclusive bohemian style updates & offers
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-border mt-12 pt-8 text-center">
          <p className="text-muted-foreground">
            © 2025 Boho Style Company. All rights reserved. Curated with love for free spirits everywhere.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default BohoFooter;